import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Trash2,
  Plus,
  Minus,
  ShoppingBag,
  ArrowRight
} from 'lucide-react';

import {
  getCart,
  updateCartQuantity,
  removeFromCart,
  imgUrl
} from '../data/mockData';

import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';


export const SHIPPING_COST = 30000; // 300 Lek


const Cart = () => {

  const [cartItems, setCartItems] =
    useState([]);


  useEffect(() => {

    loadCart();

    window.addEventListener(
      'cartUpdated',
      loadCart
    );


    return () => {

      window.removeEventListener(
        'cartUpdated',
        loadCart
      );
    };

  }, []);


  const loadCart = () => {

    setCartItems(
      getCart()
    );
  };


  // ============================================================
  // UPDATE QUANTITY
  // ============================================================

  const handleUpdateQuantity = (
    cartId,
    newQuantity,
    item
  ) => {

    if (newQuantity < 1) {
      return;
    }


    // Nëse item-i ka variant dhe kemi stock të ruajtur,
    // mos lejo sasi më të madhe se stoku.
    const maxStock =
      Number(
        item.variantStock
        ??
        item.stock
        ??
        999
      );


    if (
      maxStock > 0
      &&
      newQuantity > maxStock
    ) {

      toast.error(
        `Në stok ka vetëm ${maxStock} copë`
      );

      return;
    }


    updateCartQuantity(
      cartId,
      newQuantity
    );


    loadCart();


    window.dispatchEvent(
      new Event(
        'cartUpdated'
      )
    );
  };


  // ============================================================
  // REMOVE ITEM
  // ============================================================

  const handleRemoveItem = (
    cartId,
    productName
  ) => {

    removeFromCart(
      cartId
    );


    loadCart();


    window.dispatchEvent(
      new Event(
        'cartUpdated'
      )
    );


    toast.success(
      `${productName} u hoq nga shporta`
    );
  };


  // ============================================================
  // TOTALS
  // ============================================================

  const subtotal =
    cartItems.reduce(
      (
        total,
        item
      ) =>
        total
        +
        (
          item.price
          *
          item.quantity
        ),
      0
    );


  const shipping = SHIPPING_COST;


  const total =
    subtotal
    +
    shipping;


  // ============================================================
  // EMPTY CART
  // ============================================================

  if (
    cartItems.length === 0
  ) {

    return (

      <div className="min-h-screen bg-gray-50 flex items-center justify-center">

        <div className="text-center px-4 py-20 animate-in fade-in zoom-in duration-700">

          <ShoppingBag className="h-24 w-24 mx-auto text-gray-300 mb-6" />


          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4">

            Shporta Juaj është Bosh

          </h2>


          <p className="text-lg text-gray-600 mb-8">

            Duket se nuk keni shtuar asgjë në shportë ende.

          </p>


          <Button
            asChild
            size="lg"
            className="bg-gray-900 hover:bg-gray-800 text-white px-8 rounded-none"
          >

            <Link
              to="/collections"
              data-testid="empty-cart-shop-link"
            >

              <ShoppingBag className="mr-2 h-5 w-5" />

              Filloni Blerjet

            </Link>

          </Button>

        </div>

      </div>
    );
  }


  // ============================================================
  // CART
  // ============================================================

  return (

    <div className="min-h-screen bg-gray-50 pt-24 pb-20">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">


        {/* =====================================================
            HEADER
        ====================================================== */}

        <div className="mb-8 animate-in fade-in slide-in-from-top duration-700">

          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-gray-900 mb-2">

            Shporta e Blerjeve

          </h1>


          <p
            data-testid="cart-items-count"
            className="text-gray-600"
          >

            {
              cartItems.length
            }
            {' '}

            {
              cartItems.length === 1
                ? 'produkt'
                : 'produkte'
            }

            {' '}
            në shportën tuaj

          </p>

        </div>


        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">


          {/* =====================================================
              CART ITEMS
          ====================================================== */}

          <div className="lg:col-span-2 space-y-4">

            {cartItems.map(
              (
                item,
                index
              ) => {

                const cid =
                  item.cartId
                  ||
                  item.id;


                return (

                  <Card
                    key={cid}

                    data-testid={`cart-item-${cid}`}

                    className="border-none shadow-md hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-left"

                    style={{
                      animationDelay:
                        `${index * 100}ms`
                    }}
                  >

                    <CardContent className="p-6">

                      <div className="flex flex-col sm:flex-row gap-6">


                        {/* =========================================
                            PRODUCT IMAGE
                        ========================================== */}

                        <div className="flex-shrink-0">

                          <img
                            src={
                              imgUrl(
                                item.image
                              )
                            }

                            alt={
                              item.name
                            }

                            className="w-full sm:w-32 h-48 sm:h-32 object-cover rounded"
                          />

                        </div>


                        {/* =========================================
                            PRODUCT DETAILS
                        ========================================== */}

                        <div className="flex-grow">

                          <div className="flex justify-between items-start mb-2">

                            <div>

                              <h3 className="text-lg font-semibold text-gray-900 mb-1">

                                {item.name}

                              </h3>


                              <p className="text-sm text-gray-500">

                                {item.category}


                                {item.color && (

                                  <>
                                    {' • '}
                                    Ngjyra:
                                    {' '}
                                    <span className="font-medium text-gray-700">

                                      {item.color}

                                    </span>
                                  </>

                                )}


                                {item.size && (

                                  <>
                                    {' • '}
                                    Masa:
                                    {' '}
                                    <span className="font-medium text-gray-700">

                                      {item.size}

                                    </span>
                                  </>

                                )}

                              </p>


                              {/* VARIANT STOCK */}

                              {
                                (
                                  item.variantStock
                                  !== undefined
                                  &&
                                  item.variantStock
                                  !== null
                                )
                                &&
                                (

                                  <p className="text-xs text-gray-400 mt-1">

                                    Në stok për këtë variant:
                                    {' '}
                                    {item.variantStock}
                                    {' '}
                                    copë

                                  </p>

                                )
                              }

                            </div>


                            <button
                              data-testid={`cart-remove-${cid}`}

                              onClick={
                                () =>
                                  handleRemoveItem(
                                    cid,
                                    item.name
                                  )
                              }

                              className="text-gray-400 hover:text-red-600 transition-colors p-2"

                              aria-label="Hiq produktin"
                            >

                              <Trash2 className="h-5 w-5" />

                            </button>

                          </div>


                          <p className="text-sm text-gray-600 mb-4">

                            {item.description}

                          </p>


                          <div className="flex items-center justify-between">


                            {/* =====================================
                                QUANTITY CONTROLS
                            ====================================== */}

                            <div className="flex items-center space-x-3">

                              <button
                                data-testid={`cart-decrease-${cid}`}

                                onClick={
                                  () =>
                                    handleUpdateQuantity(
                                      cid,
                                      item.quantity - 1,
                                      item
                                    )
                                }

                                disabled={
                                  item.quantity <= 1
                                }

                                className="w-8 h-8 flex items-center justify-center border border-gray-300 hover:border-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"

                                aria-label="Ul sasinë"
                              >

                                <Minus className="h-4 w-4" />

                              </button>


                              <span
                                data-testid={`cart-quantity-${cid}`}

                                className="text-gray-900 font-medium w-8 text-center"
                              >

                                {item.quantity}

                              </span>


                              <button
                                data-testid={`cart-increase-${cid}`}

                                onClick={
                                  () =>
                                    handleUpdateQuantity(
                                      cid,
                                      item.quantity + 1,
                                      item
                                    )
                                }

                                disabled={
                                  Number(
                                    item.variantStock
                                    ??
                                    item.stock
                                    ??
                                    999
                                  ) > 0
                                  &&
                                  item.quantity
                                  >=
                                  Number(
                                    item.variantStock
                                    ??
                                    item.stock
                                    ??
                                    999
                                  )
                                }

                                className="w-8 h-8 flex items-center justify-center border border-gray-300 hover:border-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"

                                aria-label="Rrit sasinë"
                              >

                                <Plus className="h-4 w-4" />

                              </button>

                            </div>


                            {/* =====================================
                                PRICE
                            ====================================== */}

                            <div className="text-right">

                              {(
                                item.originalPrice
                                &&
                                Number(item.originalPrice) > Number(item.price)
                              ) && (
                                <p className="text-sm text-gray-400 line-through">
                                  {
                                    (
                                      Number(item.originalPrice)
                                      * item.quantity
                                      / 100
                                    ).toFixed(0)
                                  }
                                  {' '}
                                  Lek
                                </p>
                              )}

                              <p className={`text-2xl font-bold ${
                                item.originalPrice
                                &&
                                Number(item.originalPrice) > Number(item.price)
                                  ? 'text-red-600'
                                  : 'text-gray-900'
                              }`}>

                                {
                                  (
                                    (
                                      item.price
                                      *
                                      item.quantity
                                    )
                                    /
                                    100
                                  ).toFixed(0)
                                }
                                {' '}
                                Lek

                              </p>


                              {item.quantity > 1 && (

                                <p className="text-sm text-gray-500">

                                  {
                                    (
                                      item.price
                                      /
                                      100
                                    ).toFixed(0)
                                  }
                                  {' '}
                                  Lek secila

                                </p>

                              )}

                            </div>

                          </div>

                        </div>

                      </div>

                    </CardContent>

                  </Card>

                );
              }
            )}

          </div>


          {/* =====================================================
              ORDER SUMMARY
          ====================================================== */}

          <div className="lg:col-span-1">

            <Card className="border-none shadow-xl sticky top-24 animate-in fade-in slide-in-from-right duration-700">

              <CardContent className="p-8">

                <h2 className="text-2xl font-serif font-bold text-gray-900 mb-6">

                  Përmbledhja e Porosisë

                </h2>


                <div className="space-y-4 mb-6">


                  {/* SUBTOTAL */}

                  <div className="flex justify-between text-gray-600">

                    <span>
                      Nëntotali
                    </span>


                    <span
                      data-testid="cart-subtotal"
                      className="font-medium"
                    >

                      {
                        (
                          subtotal
                          /
                          100
                        ).toFixed(0)
                      }
                      {' '}
                      Lek

                    </span>

                  </div>


                  {/* SHIPPING */}

                  <div className="flex justify-between text-gray-600">

                    <span>
                      Transporti
                    </span>


                    <span
                      data-testid="cart-shipping"
                      className="font-medium"
                    >

                      {
                        shipping === 0

                          ? 'Falas'

                          : `${
                              (
                                shipping
                                /
                                100
                              ).toFixed(0)
                            } Lek`
                      }

                    </span>

                  </div>


                  {/* TOTAL */}

                  <div className="border-t border-gray-200 pt-4">

                    <div className="flex justify-between text-gray-900">

                      <span className="text-lg font-semibold">

                        Totali

                      </span>


                      <span
                        data-testid="cart-total"
                        className="text-2xl font-bold"
                      >

                        {
                          (
                            total
                            /
                            100
                          ).toFixed(0)
                        }
                        {' '}
                        Lek

                      </span>

                    </div>

                  </div>

                </div>


                {/* CHECKOUT */}

                <Button
                  asChild
                  size="lg"

                  className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-none py-6 mb-4 transition-all duration-300 hover:scale-105"
                >

                  <Link
                    to="/checkout"
                    data-testid="proceed-to-checkout-btn"
                  >

                    Vazhdo me Porosinë

                    <ArrowRight className="ml-2 h-5 w-5" />

                  </Link>

                </Button>


                {/* CONTINUE SHOPPING */}

                <Button
                  asChild
                  variant="outline"
                  size="lg"

                  className="w-full border-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium rounded-none py-6"
                >

                  <Link
                    to="/collections"
                    data-testid="continue-shopping-btn"
                  >

                    Vazhdoni Blerjet

                  </Link>

                </Button>


                {/* FREE SHIPPING */}

                <div className="mt-6 pt-6 border-t border-gray-200">

                  <p className="text-sm text-gray-500 text-center">

                    Transporti për çdo porosi është 300 Lek

                  </p>

                </div>

              </CardContent>

            </Card>

          </div>

        </div>

      </div>

    </div>
  );
};


export default Cart;